import { GradeNotificationType, UrgentRelationship } from '@gyym/sdk'
import { useGyymClient } from '@gyym/sdk-react'
import { Modal, useModal } from '@gyym/ui'
import { useRouter } from 'next/navigation'
import React from 'react'

import { ProfileForm } from '@/components/Forms/ProfileForm'
import { GyymPassModal } from '@/components/Modals/GyymPassModal'
import { consumeSuppressProfileModalAfterLogin } from '@/modules/Passkey/utils'
import { getPrefectureById } from '@/modules/Prefectures'

import { useAuthUser } from './useAuthUser'
import { useIntendedPath } from './useIntendedPath'

export const useInitialProfileRegistrationEffect = () => {
  const {
    authUser,
    isLoggedIn,
    isEmailVerified,
    isProfileRegistered,
    revalidate,
  } = useAuthUser()

  const intended = useIntendedPath()
  const router = useRouter()

  const { openModal } = useModal()
  const client = useGyymClient()

  // ログイン直後のランディングではプロフィール登録モーダルを抑止しパスキー訴求のみ表示する。
  // マウント毎に1度だけ抑止マーカーを消費して判定を固定し、effect の再実行や
  // FacilitySearchView の遅延マウント（Watcher との実行順の揺れ）に影響されないようにする。
  const suppressOnLandingRef = React.useRef<boolean | null>(null)

  React.useEffect(() => {
    // メール認証（新規登録）のランディングは useVerifyEmailEffect が所有し、
    // パスキー登録訴求モーダルを表示する。モーダルは単一スロット（後勝ち）のため、
    // ここでプロフィール登録モーダルを開くと訴求モーダルを上書きしてしまう。
    // verify クエリがある間は抑止する（sessionStorage の抑止フラグはメールリンク経由の
    // 別セッションには効かないため、URL クエリで確実に判定する）。
    const verifyParams = new URLSearchParams(window.location.search)
    if (
      verifyParams.get('key') &&
      verifyParams.get('expires') &&
      verifyParams.get('signature')
    ) {
      return
    }

    if (suppressOnLandingRef.current === null) {
      suppressOnLandingRef.current = consumeSuppressProfileModalAfterLogin()
    }
    if (suppressOnLandingRef.current) return

    if (authUser && isLoggedIn && isEmailVerified && !isProfileRegistered) {
      openModal(Modal, {
        title: 'プロフィール登録',
        baseWidth: 500,
        expandOnSmallDevices: true,
        children: (
          <ProfileForm
            onSubmit={async (values) => {
              await client.user.profiles.update(authUser.id, {
                ...values,
                address2: values.address2 ?? '',
                prefecture: getPrefectureById(values.prefecture)!.name,
                urgentRelationship:
                  values.urgentRelationship === UrgentRelationship.OTHER
                    ? values.urgentRelationshipOther
                    : values.urgentRelationship,
              })

              const user = await revalidate()

              if (!user || !user.progress) return

              if (intended) {
                router.push(intended)
                return
              }

              openModal(GyymPassModal, {
                grade: user.progress.currentGrade,
                type: GradeNotificationType.GRADE_UP,
                isAfterProfileUpdate: true,
                disableBackdropClose: false,
              })
            }}
          >
            {({ fields, submit }) => (
              <>
                <Modal.Body>{fields}</Modal.Body>
                <Modal.Footer>{submit}</Modal.Footer>
              </>
            )}
          </ProfileForm>
        ),
      })
    }
  }, [
    authUser,
    client.user.profiles,
    isEmailVerified,
    isLoggedIn,
    router,
    intended,
    isProfileRegistered,
    openModal,
    revalidate,
  ])
}
